import { motion } from 'framer-motion';
import { EASE_EXPO, VIEWPORT_TIGHT } from '@/lib/motion';

/**
 * Proportional meter for a figure that has already been stated in words.
 *
 * The deck quotes a lot of numbers whose *relative* size carries the
 * argument — 18,525 CVLs against 186 CVAs, a £180k saving against £4.5m,
 * a £5.45m floor under a £77.9m ceiling. Rendered as type alone they all
 * occupy the same visual weight, so the reader has to do the arithmetic
 * before the point lands. A bar does that comparison pre-attentively.
 *
 * This adds no claim the page wasn't already making: every width is
 * derived from a figure printed next to it.
 *
 * `from`/`to` are fractions of the same shared scale (0–1). Passing both
 * draws a floating band, which is how an honest range is shown — a range
 * has no single "value" to plot, and picking its midpoint would invent a
 * precision the source doesn't have.
 */
export function ScaleBar({
  to,
  from = 0,
  delay = 0,
  tone = 'brand',
  className = '',
}: {
  /** Upper edge of the bar, as a fraction of the group's scale. */
  to: number;
  /** Lower edge. Non-zero draws a band rather than a bar from the origin. */
  from?: number;
  delay?: number;
  tone?: 'brand' | 'muted';
  className?: string;
}) {
  const left = `${Math.max(0, Math.min(1, from)) * 100}%`;
  const width = `${Math.max(0, Math.min(1, to - from)) * 100}%`;

  return (
    <div
      aria-hidden
      className={`relative h-[3px] w-full overflow-hidden rounded-full bg-white/[0.06] ${className}`}
    >
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        whileInView={{ width, opacity: 1 }}
        viewport={VIEWPORT_TIGHT}
        transition={{
          width: { duration: 1.15, ease: EASE_EXPO, delay },
          opacity: { duration: 0.3, delay },
        }}
        style={{ left }}
        className={`absolute inset-y-0 rounded-full ${
          tone === 'brand'
            ? 'bg-gradient-to-r from-brand-500 to-brand-bright shadow-[0_0_12px_rgba(96,165,250,0.45)]'
            : 'bg-border-strong'
        }`}
      />
    </div>
  );
}
